import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ArrowLeft, Printer, Download, Calendar, User, FileText, Filter,
} from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

const fmt = (n) =>
  (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

const TYPE_LABEL = {
  invoice:      { en: 'Invoice',     ar: 'فاتورة',     color: 'text-blue-700  bg-blue-50' },
  receipt:      { en: 'Receipt',     ar: 'سند قبض',   color: 'text-green-700 bg-green-50' },
  payment:      { en: 'Payment',     ar: 'دفعة',       color: 'text-green-700 bg-green-50' },
  credit_note:  { en: 'Credit Note', ar: 'إشعار دائن', color: 'text-amber-700 bg-amber-50' },
};

const CustomerStatement = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [statement, setStatement] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [activeId, setActiveId] = useState(customerId || searchParams.get('customer') || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilter, setShowFilter] = useState(false);

  // Load list of customers for the selector.
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get('/customers');
        setCustomers(res.data || []);
        // If URL had no id, default to the first customer.
        if (!activeId && (res.data || []).length > 0) {
          setActiveId(res.data[0].id);
        }
      } catch {
        toast.error('Failed to load customers list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the statement whenever the active customer or date range changes.
  useEffect(() => {
    if (!activeId) {
      setLoading(false);
      setStatement(null);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();
        if (dateFrom) params.append('date_from', dateFrom);
        if (dateTo) params.append('date_to', dateTo);
        const qs = params.toString();
        const url = `/accounting/customer-statement/${activeId}${qs ? '?' + qs : ''}`;
        const res = await axiosInstance.get(url);
        setStatement(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load statement');
        setStatement(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [activeId, dateFrom, dateTo]);

  const downloadPDF = () => {
    if (!statement) return;
    const doc = new jsPDF();
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(107, 33, 168);
    doc.text('Customer Statement', 105, 15, { align: 'center' });
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.text(processArabicText('كشف حساب عميل'), 105, 22, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(60);
    const p = statement.party;
    const t = statement.totals;
    const header = [
      [`Customer: ${p.name}`, `Code: ${p.code || '-'}`],
      [`VAT: ${p.vat_number || '-'}`, `Period: ${statement.date_from || '—'}  →  ${statement.date_to || '—'}`],
      [`Opening: ${fmt(t.opening_balance)} SAR`, `Closing: ${fmt(t.closing_balance)} SAR`],
    ];
    autoTable(doc, {
      startY: 28,
      body: header,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 1 },
      columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 95 } },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      head: [['Date', 'Reference', 'Type', 'Description', 'Debit', 'Credit', 'Balance']],
      body: statement.entries.map((e) => [
        e.date, e.reference || '-', e.type, e.description || '',
        e.debit ? fmt(e.debit) : '', e.credit ? fmt(e.credit) : '', fmt(e.balance),
      ]),
      headStyles: { fillColor: [107, 33, 168], textColor: 255, fontSize: 9 },
      styles: { fontSize: 8 },
      columnStyles: { 4: { halign: 'right' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 4,
      body: [
        ['Total Invoiced',     fmt(t.total_invoiced)     + ' SAR'],
        ['Total Paid',         fmt(t.total_paid)         + ' SAR'],
        ['Total Credit Notes', fmt(t.total_credit_notes) + ' SAR'],
        ['Closing Balance',    fmt(t.closing_balance)    + ' SAR'],
      ],
      theme: 'striped',
      styles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold' }, 1: { halign: 'right', fontStyle: 'bold' } },
    });

    doc.save(`customer-statement-${p.code || p.id}-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success('PDF downloaded');
  };

  const activeCustomer = useMemo(
    () => customers.find((c) => c.id === activeId),
    [customers, activeId],
  );

  if (loading && !statement) {
    return (
      <div className="p-6 text-center text-gray-500" data-testid="customer-statement-loading">
        Loading…
      </div>
    );
  }

  const p = statement?.party;
  const t = statement?.totals;
  const a = statement?.aging;

  return (
    <div className="p-6 space-y-6 max-w-screen-2xl mx-auto" data-testid="customer-statement-page">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            data-testid="customer-statement-back"
            onClick={() => navigate('/customer-report')}
            className="p-2 rounded-lg border bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-purple-700">Customer Statement</h1>
            <p className="text-sm text-gray-500" dir="rtl">كشف حساب عميل</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="customer-statement-selector"
            value={activeId}
            onChange={(e) => setActiveId(e.target.value)}
            className="border rounded-lg px-3 py-2 bg-white text-sm min-w-[260px]"
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.customer_number ? `${c.customer_number} — ` : ''}
                {c.name_english || c.name_arabic || c.name}
              </option>
            ))}
          </select>
          <button
            data-testid="customer-statement-filter-toggle"
            onClick={() => setShowFilter(!showFilter)}
            className="border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm flex items-center gap-1"
          >
            <Filter className="w-4 h-4" /> {dateFrom || dateTo ? 'Filtered' : 'Date Range'}
          </button>
          <button
            data-testid="customer-statement-print"
            onClick={() => window.print()}
            className="border rounded-lg px-3 py-2 bg-white hover:bg-gray-50 text-sm flex items-center gap-1"
          >
            <Printer className="w-4 h-4" /> Print
          </button>
          <button
            data-testid="customer-statement-pdf"
            onClick={downloadPDF}
            className="bg-purple-600 text-white rounded-lg px-3 py-2 text-sm hover:bg-purple-700 flex items-center gap-1"
          >
            <Download className="w-4 h-4" /> PDF
          </button>
        </div>
      </div>

      {showFilter && (
        <div className="bg-white border rounded-lg p-4 flex items-end gap-3 flex-wrap" data-testid="customer-statement-filter-panel">
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                   className="border rounded px-2 py-1 text-sm" data-testid="customer-statement-date-from" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                   className="border rounded px-2 py-1 text-sm" data-testid="customer-statement-date-to" />
          </div>
          <button
            data-testid="customer-statement-clear-dates"
            onClick={() => { setDateFrom(''); setDateTo(''); }}
            className="text-xs text-gray-500 hover:text-gray-800 underline"
          >
            Clear
          </button>
        </div>
      )}

      {!statement && (
        <div className="p-12 text-center text-gray-500 border rounded-lg bg-white" data-testid="customer-statement-empty">
          No customer selected.
        </div>
      )}

      {statement && (
        <>
          {/* Header card */}
          <div className="bg-white border rounded-xl p-5 shadow-sm" data-testid="customer-statement-header">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="text-xs uppercase text-gray-400 tracking-wide">Customer</div>
                <div className="text-lg font-bold text-gray-900 mt-1">{p.name}</div>
                {p.name_ar && <div className="text-sm text-gray-600" dir="rtl">{p.name_ar}</div>}
                <div className="text-xs text-gray-500 mt-1">Code: <span className="font-mono">{p.code || '—'}</span></div>
                {p.vat_number && <div className="text-xs text-gray-500">VAT: <span className="font-mono">{p.vat_number}</span></div>}
              </div>
              <div>
                <div className="text-xs uppercase text-gray-400 tracking-wide">Period</div>
                <div className="mt-1 text-sm">
                  <Calendar className="inline w-4 h-4 mr-1 text-gray-400" />
                  {statement.date_from || 'Beginning'} <span className="text-gray-400 mx-1">→</span> {statement.date_to || 'Today'}
                </div>
                <div className="text-xs text-gray-500 mt-1">As of: {statement.as_of}</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <SummaryTile label="Opening"        value={t.opening_balance} accent="gray" />
                <SummaryTile label="Invoiced"       value={t.total_invoiced}  accent="blue" />
                <SummaryTile label="Paid"           value={t.total_paid}      accent="green" />
                <SummaryTile label="Credit Notes"   value={t.total_credit_notes} accent="amber" />
                <SummaryTile label="Closing"        value={t.closing_balance} accent="purple" wide />
              </div>
            </div>
          </div>

          {/* Aging */}
          <div className="bg-white border rounded-xl p-4 shadow-sm" data-testid="customer-statement-aging">
            <div className="text-xs uppercase text-gray-400 tracking-wide mb-2">Aging Analysis (as of {statement.as_of})</div>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <AgingTile label="Current"  value={a.current}     />
              <AgingTile label="1-30 d"   value={a.d_1_30}     />
              <AgingTile label="31-60 d"  value={a.d_31_60}    />
              <AgingTile label="61-90 d"  value={a.d_61_90}    />
              <AgingTile label="91-120 d" value={a.d_91_120}   />
              <AgingTile label="120+ d"   value={a.d_120_plus} danger />
            </div>
          </div>

          {/* Subledger table */}
          <div className="bg-white border rounded-xl shadow-sm overflow-hidden" data-testid="customer-statement-table">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left  px-4 py-2">Date</th>
                  <th className="text-left  px-4 py-2">Reference</th>
                  <th className="text-left  px-4 py-2">Type</th>
                  <th className="text-left  px-4 py-2">Description</th>
                  <th className="text-right px-4 py-2">Debit</th>
                  <th className="text-right px-4 py-2">Credit</th>
                  <th className="text-right px-4 py-2">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t bg-gray-50/40" data-testid="customer-statement-opening-row">
                  <td className="px-4 py-2 text-gray-500" colSpan={6}>
                    Opening Balance
                  </td>
                  <td className="px-4 py-2 text-right font-semibold">{fmt(statement.opening_balance)}</td>
                </tr>
                {statement.entries.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      No transactions in this period.
                    </td>
                  </tr>
                )}
                {statement.entries.map((e, i) => {
                  const tl = TYPE_LABEL[e.type] || { en: e.type, color: 'text-gray-700 bg-gray-100' };
                  return (
                    <tr key={i} className="border-t hover:bg-purple-50/30" data-testid={`customer-statement-row-${i}`}>
                      <td className="px-4 py-2 text-gray-700">{e.date}</td>
                      <td className="px-4 py-2 font-mono text-xs text-purple-700">{e.reference || '—'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${tl.color}`}>{tl.en}</span>
                      </td>
                      <td className="px-4 py-2 text-gray-600 max-w-md truncate" title={e.description}>{e.description || '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums text-blue-700">
                        {e.debit ? fmt(e.debit) : ''}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums text-green-700">
                        {e.credit ? fmt(e.credit) : ''}
                      </td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmt(e.balance)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t bg-purple-50" data-testid="customer-statement-closing-row">
                  <td className="px-4 py-2 font-bold text-purple-700" colSpan={4}>Closing Balance</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{fmt(statement.entries.reduce((s, e) => s + (e.debit || 0), 0))}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold">{fmt(statement.entries.reduce((s, e) => s + (e.credit || 0), 0))}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-bold text-purple-700">{fmt(t.closing_balance)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

const accentStyles = {
  gray:   'bg-gray-50  text-gray-700  border-gray-200',
  blue:   'bg-blue-50  text-blue-700  border-blue-200',
  green:  'bg-green-50 text-green-700 border-green-200',
  amber:  'bg-amber-50 text-amber-700 border-amber-200',
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
};

const SummaryTile = ({ label, value, accent = 'gray', wide = false }) => (
  <div className={`border rounded-lg p-2 ${accentStyles[accent]} ${wide ? 'col-span-2' : ''}`} data-testid={`customer-statement-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-base font-bold tabular-nums">{fmt(value)} <span className="text-xs opacity-60">SAR</span></div>
  </div>
);

const AgingTile = ({ label, value, danger = false }) => (
  <div className={`border rounded-lg p-2 text-center ${danger && value > 0 ? 'bg-red-50 border-red-300 text-red-700' : 'bg-gray-50'}`}>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="font-bold tabular-nums">{fmt(value)}</div>
  </div>
);

export default CustomerStatement;
